import { lineString } from "@turf/turf";
import along from "@turf/along";
import { v4 as uuid } from "uuid";
import getAssetStyleInfo from "../context/AssetStyleInfo";
import {
  getStartIsUpstream,
  getTotalAdditionalUpstreamLength,
  getTotalAdditionalDownstreamLength,
  getGeographicCableLengthExcludingAdditions,
  getResolvedCableLengthExcludingAdditions,
  getResolvedCableLengthIncludingAdditions,
} from "../utils/cableFunctions";

export const mapCableRouteBackToTransformer = (networkCables, faultyCable) => {
  const cables = [];
  cables.push(faultyCable);
  let assetId = faultyCable.upstreamAssetId;
  while (!!assetId) {
    const cable = networkCables.find((n) => n.downstreamAssetId === assetId);
    if (!cable) {
      break;
    }
    assetId = cable.upstreamAssetId;
    if (cables.some((c) => c.id === cable.id)) {
      break;
    }
    cables.push(cable);
  }
  return cables;
};

export const getFaultMarker = (cable, cables, allAssetsWhichCanConnectToCables, color) => {
  return createMarker(
    getFaultMarkerLocation(cable),
    calcLengthAlongCableToFault(cable),
    getResolvedCableLengthIncludingAdditions(cable),
    cables,
    cable,
    allAssetsWhichCanConnectToCables,
    color,
  );
};

const getFaultMarkerLocation = (cable) => {
  const line = lineString(cable.geometry.map((a) => [a.lng, a.lat]));

  const geographicCableLength = getGeographicCableLengthExcludingAdditions(cable);

  const markerPercentageAlongCable = calcMarkerPositionPercentageAlongCable(cable);

  // The 'start' of the cable may be downstream end if the cable was drawn backwards.
  const percentageFromStartOfCableCable = getStartIsUpstream(cable)
    ? markerPercentageAlongCable
    : 100 - markerPercentageAlongCable;
  const lengthToMarkerPositionFromStart =
    (geographicCableLength / 100) * percentageFromStartOfCableCable;

  const markerLocation = along(line, lengthToMarkerPositionFromStart, {
    units: "meters",
  });
  return markerLocation;
};

const getDistanceFromFaultToTransformer = (cables, lengthToFaultOnFaultyCable, faultyCableId) => {
  var distance = 0;

  cables
    .filter((c) => {
      return c.id !== faultyCableId;
    })
    .forEach((cable) => (distance += getResolvedCableLengthIncludingAdditions(cable)));

  return distance + lengthToFaultOnFaultyCable;
};

const getImpedanceAtNodes = (cable) => {
  return cable.impedanceType === "Loop"
    ? [cable.upstreamNodeLoopImpedance, cable.downstreamNodeLoopImpedance]
    : [cable.upstreamNodePhaseImpedance, cable.downstreamNodePhaseImpedance];
};

export const calcMarkerPositionPercentageAlongCable = (cable) => {
  const regularCableLength = getResolvedCableLengthExcludingAdditions(cable);
  const additionalUpstreamLength = getTotalAdditionalUpstreamLength(cable);
  const additionalDownstreamLength = getTotalAdditionalDownstreamLength(cable);

  const totalCableLength =
    regularCableLength + additionalUpstreamLength + additionalDownstreamLength;

  if (totalCableLength <= 0) {
    return 0;
  }

  const nodeImpedances = getImpedanceAtNodes(cable);
  const upstreamNodeImpedance = Math.min(nodeImpedances[0], nodeImpedances[1]);
  const downstreamNodeImpedance = Math.max(nodeImpedances[0], nodeImpedances[1]);
  const totalImpedanceRange = downstreamNodeImpedance - upstreamNodeImpedance;

  const impedanceAtStartOfRegularCable =
    (totalImpedanceRange * additionalUpstreamLength) / totalCableLength;
  const impedanceAtEndOfRegularCable =
    (totalImpedanceRange * (additionalUpstreamLength + regularCableLength)) / totalCableLength;
  const regularImpedanceRange = impedanceAtEndOfRegularCable - impedanceAtStartOfRegularCable;

  if (regularImpedanceRange <= 0) {
    return 0;
  }

  const impedanceToLocate = parseFloat(cable.impedance.impedance) - upstreamNodeImpedance;

  const clampPercentage = (value) => Math.max(0, Math.min(100, value));

  // Find the location of the marker as a percentage of the distance along the cable from the upstream node.
  // Values of < 0% or > 100% happen when the fault is located on the additional cable segments at the nodes.
  // Hence clamp to 0% - 100% range so that the marker appears at the nodes in these cases.
  const percentageAlongCable = clampPercentage(
    ((impedanceToLocate - impedanceAtStartOfRegularCable) / regularImpedanceRange) * 100,
  );

  return percentageAlongCable;
};

export const calcLengthAlongCableToFault = (cable) => {
  const totalCableLength = getResolvedCableLengthIncludingAdditions(cable);
  const nodeImpedances = getImpedanceAtNodes(cable);
  const upstreamNodeImpedance = Math.min(nodeImpedances[0], nodeImpedances[1]);
  const downstreamNodeImpedance = Math.max(nodeImpedances[0], nodeImpedances[1]);
  const totalImpedanceRange = downstreamNodeImpedance - upstreamNodeImpedance;

  if (totalImpedanceRange <= 0) {
    return 0;
  }

  const impedanceToLocate = parseFloat(cable.impedance.impedance) - upstreamNodeImpedance;

  const ratio = impedanceToLocate / totalImpedanceRange;
  const lengthToFault = totalCableLength * ratio;

  return Math.max(0, Math.min(totalCableLength, lengthToFault));
};

const createMarker = (
  markerLocation,
  lengthToMarkerPosition,
  fullCableLength,
  cables,
  cable,
  allAssetsWhichCanConnectToCables,
  color,
) => {
  const useUpstreamNode = lengthToMarkerPosition * 2 < fullCableLength;

  const marker = {
    id: uuid(),
    styles: getAssetStyleInfo("map-marker"),
    geometry: {
      lat: markerLocation.geometry.coordinates[1],
      lng: markerLocation.geometry.coordinates[0],
    },
    distanceToTransformer: getDistanceFromFaultToTransformer(
      cables,
      lengthToMarkerPosition,
      cable.id,
    ),
    distanceToClosestNode: useUpstreamNode
      ? lengthToMarkerPosition
      : fullCableLength - lengthToMarkerPosition,
    node: getAsset(allAssetsWhichCanConnectToCables, cable, useUpstreamNode),
    color: color,
    impedance: cable.impedance.impedance,
  };

  return marker;
};

const getAsset = (allAssetsWhichCanConnectToCables, cable, useUpstreamNode) => {
  if (useUpstreamNode) {
    return allAssetsWhichCanConnectToCables.find((a) => a.id === cable.upstreamAssetId);
  }
  return allAssetsWhichCanConnectToCables.find((a) => a.id === cable.downstreamAssetId);
};
